import { _rk } from "../../core";
import { debounce } from "../../debounce";
import { $ } from "../../dom";
import { Input, type InputOptions } from "../core";

export type TextInputOptions<TValue> = Omit<
  InputOptions<TValue>,
  "resetValue" | "$findInput"
> & {
  autoComplete?: boolean;

  typingInputDebounceDelay?: number;
};

export type TextInputAutoCompleteOptions = {
  id: string;

  getSuggestions: (value: string) => Promise<string[]> | string[];

  $suggestionMenu: $;
};

export class TextInput extends Input<string | null> {
  constructor($root: $, options: TextInputOptions<string | null> = {}) {
    const {
      autoComplete = "off",
      typingInputDebounceDelay = 0,
      ...superOptions
    } = options;

    super($root, {
      resetValue: null,
      $findInput: ($root) =>
        $root.find("input[type='text'], input[type='search']", true),
      ...superOptions,
    });

    this.$input.attr("autocomplete", autoComplete);

    this.$input.on(
      "input:relkit-input",
      debounce(() => this.triggerInput(), typingInputDebounceDelay),
    );
    this.$input.on("change:relkit-input", () => this.triggerChange());
  }

  get value(): string | null {
    return this.$input.value || null;
  }

  set value(value: string | null) {
    this.$input.value = value ?? "";

    this.triggerInput();
    this.triggerChange();
  }

  get data(): string | null {
    return this.value;
  }

  get isActive(): boolean {
    return this.value !== null;
  }

  autocomplete(options: TextInputAutoCompleteOptions): void {
    const { id, getSuggestions, $suggestionMenu } = options;

    const inputId = _rk("combobox", "list", id);

    let currentActiveIndex = 0;

    const optionIdAt = (index: number) => `${inputId}-opt-${index}`;

    this.$input.attr("role", "combobox");
    this.$input.attr("aria-haspopup", "listbox");
    this.$input.attr("aria-autocomplete", "list");
    this.$input.attr("aria-controls", inputId);

    const suggestionTemplate = $suggestionMenu.template("suggestion");

    if (!suggestionTemplate) {
      return;
    }

    suggestionTemplate.$root.attr("id", inputId);
    suggestionTemplate.$root.attr("role", "listbox");

    const setActiveIndex = (index: number) => {
      currentActiveIndex = index;

      const $options = suggestionTemplate.$clones("suggestion");

      $options.attr("aria-selected", "false").flag("active", false);

      this.$input.attr("aria-activedescendant", null);

      if (currentActiveIndex >= 0) {
        const $option = $options.eq(currentActiveIndex);

        $option.attr("aria-selected", "true").flag("active", true);

        this.$input.attr(
          "aria-activedescendant",
          optionIdAt(currentActiveIndex),
        );
      }
    };

    const close = () => {
      suggestionTemplate.empty();

      setActiveIndex(-1);

      this.$input.attr("aria-expanded", "false");

      $suggestionMenu.toggle(false);
    };

    const tryOpen = async () => {
      close();

      if ($suggestionMenu.length === 0) {
        return;
      }

      if (!this.value) {
        return;
      }

      const suggestions = await getSuggestions(this.value);

      if (suggestions.length === 0) {
        return;
      }

      suggestionTemplate.empty();
      suggestionTemplate.fromArray(
        suggestions,
        (suggestion, $suggestion, index) => {
          $suggestion.attr("id", optionIdAt(index));
          $suggestion.attr("role", "option");
          $suggestion.attr("aria-selected", "false");

          $suggestion.$text("value").text = suggestion;

          $suggestion.on("click", () => {
            this.value = suggestion;

            close();
          });

          $suggestion.on("mouseenter", () => setActiveIndex(index));
          $suggestion.on("mousemove", () => setActiveIndex(index));
        },
      );

      setActiveIndex(-1);

      this.$input.attr("aria-expanded", "true");

      $suggestionMenu.toggle(true);
    };

    this.onInput(() => {
      tryOpen();
    });

    let pointerDownInMenu = false;

    suggestionTemplate.$root.on("pointerdown", () => {
      pointerDownInMenu = true;
    });
    suggestionTemplate.$root.on("mousedown", () => {
      pointerDownInMenu = true;
    });

    this.$input.on("focus", () => {
      tryOpen();
    });
    this.$input.on("blur", () => {
      setTimeout(() => {
        if (!pointerDownInMenu) {
          close();
        }

        pointerDownInMenu = false;
      });
    });

    this.$input.on("keydown", (event) => {
      const $options = suggestionTemplate.$clones("suggestion");
      const optionCount = $options.length;

      if (event.key === "ArrowDown") {
        if (currentActiveIndex < optionCount - 1) {
          event.preventDefault();
          setActiveIndex(currentActiveIndex + 1);
        }
      } else if (event.key === "ArrowUp") {
        if (currentActiveIndex > 0) {
          event.preventDefault();
          setActiveIndex(currentActiveIndex - 1);
        }
      } else if (event.key === "Enter") {
        event.preventDefault();
        if (currentActiveIndex >= 0) {
          $options.eq(currentActiveIndex).trigger("click");
        }
      } else if (event.key === "Escape") {
        event.preventDefault();
        close();
      } else if (event.key === "Tab") {
        close();
      }
    });

    close();
  }

  destroy(): void {
    super.destroy();

    throw new Error("Not implemented"); // TODO: Implement destroy for autocomplete
  }
}
